/**
 * Computes the SHA-256 hash of the input string
 * @param input - The string to hash
 * @returns The SHA-256 hash of the input string
 */
export const calculateStrChecksum = async (input: string): Promise<string> => {
	const encoder = new TextEncoder();
	const data = encoder.encode(input);
	const hash = await window.crypto.subtle.digest('SHA-256', data);
	const hashArray = Array.from(new Uint8Array(hash));
	const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
	return hashHex;
};

/**
 * Encrypts data using AES-256-GCM
 * @param data - The string data to encrypt
 * @param key - The encryption key (string)
 * @returns Promise resolving to Base64(IV + Ciphertext + AuthTag)
 */
export const encryptAES256GCM = async (data: string, key: string): Promise<string> => {
  const encoder = new TextEncoder();
  const dataBytes = encoder.encode(data);
  
  // Hash the key to ensure it's 256 bits (32 bytes) for AES-256
  const keyBytes = await window.crypto.subtle.digest('SHA-256', encoder.encode(key));
  
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  const encryptedContent = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    cryptoKey,
    dataBytes
  );

  // Combine IV + Ciphertext (AuthTag is included in ciphertext by Web Crypto for AES-GCM)
  // Note: Web Crypto API appends the auth tag at the end of the ciphertext automatically for AES-GCM
  
  const encryptedBytes = new Uint8Array(encryptedContent);
  const result = new Uint8Array(iv.length + encryptedBytes.length);
  result.set(iv, 0);
  result.set(encryptedBytes, iv.length);

  // Convert to Base64
  return btoa(String.fromCharCode(...Array.from(result)));
};

/**
 * Decrypts data using AES-256-GCM
 * @param encryptedData - The Base64 encoded string (IV + Ciphertext + AuthTag)
 * @param key - The encryption key (string)
 * @returns Promise resolving to the decrypted string
 */
export const decryptAES256GCM = async (encryptedData: string, key: string): Promise<string> => {
  const encoder = new TextEncoder();
  
  // Prepare key
  const keyBytes = await window.crypto.subtle.digest('SHA-256', encoder.encode(key));
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM' },
    false,
    ['decrypt']
  );

  // Decode Base64
  const binaryString = atob(encryptedData);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Extract IV
  const iv = bytes.slice(0, 12);
  const ciphertext = bytes.slice(12);

  const decryptedContent = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    cryptoKey,
    ciphertext
  );

  const decoder = new TextDecoder();
  return decoder.decode(decryptedContent);
};
